import React, { useState, useCallback, useRef } from "react";
import { message } from "antd";

import AddAttachmentModal from "components/add-attachment-modal/add-attachment-modal";
import AttachmentItem from "components/attachment-item/attachment-item";
import MediaPlayer from "components/media-player/media-player";

import { MediaType } from "constants/field-values";

import { useSelectedAttachments, clearSelectedAttachments } from "state/reactive-vars/selected-attachment-var";
import { createFirebaseFileName, uploadToStorage } from "utils/files-management";
import { Property_Documents_Insert_Input } from "graphql/generated/graphql";
import { useAddPropertyDocumentsMutation } from "graphql/mutations/add-property-documents.generated";
import { useDeletePropertyDocumentByIdMutation } from "graphql/mutations/delete-property-document-by-id.generated";

interface HandoverAdditionalResourcesProps {
	propertyId: string;
	handoverId: string;
	propertyDocuments: any[];
	canEdit: boolean;
	isAnon?: boolean;
}

const getDocumentBadge = (mimeType?: string | null): string => {
	if (!mimeType) return "DOCUMENT";
	if (mimeType.startsWith("video/")) return "VIDEO";
	if (mimeType.startsWith("image/")) return "IMAGE";
	if (mimeType === "application/pdf") return "PDF";
	return "DOCUMENT";
};

const HandoverAdditionalResources: React.FC<HandoverAdditionalResourcesProps> = ({
	propertyId,
	handoverId,
	propertyDocuments = [],
	canEdit,
	isAnon = false
}) => {
	const { updateSelectedAttachments } = useSelectedAttachments();
	const [addDocumentModalOpen, setAddDocumentModalOpen] = useState(false);
	const [addDocumentModalEditMode, setAddDocumentModalEditMode] = useState(false);
	const [documentsFiles, setDocumentsFiles] = useState<Array<any>>([]);
	const [uploading, setUploading] = useState(false);
	const [fileViewer, setFileViewer] = useState<{ type: MediaType | null; src: string }>({ type: null, src: "" });

	// Ref para leer documentsFiles síncronamente desde handleOnConfirmDocumentModal.
	// setDocumentsFiles es asíncrono: cuando onConfirm es invocado por AddAttachmentModal
	// justo después de llamar updateAttachments, el estado aún no se actualizó.
	const documentsFilesRef = useRef<Array<any>>([]);

	// Add property documents mutation
	const [addPropertyDocumentsMutation, { loading: loadingAdd }] = useAddPropertyDocumentsMutation({
		refetchQueries: ["getHandoverById"],
		onCompleted: () => {
			message.success("Documents uploaded successfully");
			setDocumentsFiles([]);
			documentsFilesRef.current = [];
		},
		onError: error => {
			console.error("Error uploading documents:", error);
			message.error("Failed to upload documents");
		}
	});

	// Delete property document mutation
	const [deletePropertyDocumentByIdMutation, { loading: loadingDelete }] = useDeletePropertyDocumentByIdMutation({
		refetchQueries: ["getHandoverById"],
		onCompleted: () => {
			message.success("Document deleted successfully");
		},
		onError: error => {
			console.error("Error deleting document:", error);
			message.error("Failed to delete document");
		}
	});

	const handleOnCloseDocumentModal = useCallback(() => {
		setAddDocumentModalOpen(false);
		setAddDocumentModalEditMode(false);
		clearSelectedAttachments();
	}, []);

	const handleOnRemoveDocument = useCallback(
		(documentFile: any) => {
			const identifier =
				documentFile.uid ||
				documentFile.photo_link ||
				documentFile.video_link ||
				documentFile.document_link ||
				documentFile.audio_link;

			const newList = documentsFiles.filter(element => {
				const elementId =
					element.uid || element.photo_link || element.video_link || element.document_link || element.audio_link;
				return elementId !== identifier;
			});
			documentsFilesRef.current = newList;
			setDocumentsFiles(newList);
		},
		[documentsFiles]
	);

	const handleOnEditDocument = useCallback(
		(documentFile: any) => {
			updateSelectedAttachments(documentFile);
			setAddDocumentModalEditMode(true);
			setAddDocumentModalOpen(true);
		},
		[updateSelectedAttachments]
	);

	const updateDocuments = useCallback((documentFile: any) => {
		const { uid, name, file, type } = documentFile;

		const newDocument = {
			uid,
			name,
			type,
			file,
			mime_type: file.type,
			originFileObj: file.originFileObj,
			localUrl: URL.createObjectURL(file.originFileObj)
		};

		// 1. Update the reference synchronously (so that handleOnConfirmDocumentModal can read it)
		documentsFilesRef.current = [...documentsFilesRef.current.filter(element => element.uid !== uid), newDocument];

		// 2. Reflect in state (for visual feedback with AttachmentItem during upload)
		setDocumentsFiles(documentsFilesRef.current);
	}, []);

	const handleDeleteExistingDocument = useCallback(
		async (documentId: string) => {
			await deletePropertyDocumentByIdMutation({
				variables: { id: documentId }
			});
		},
		[deletePropertyDocumentByIdMutation]
	);

	const handleUploadDocuments = useCallback(async () => {
		if (documentsFilesRef.current.length === 0) return;

		setUploading(true);

		try {
			// Map each document to an upload promise
			const uploadPromises = documentsFilesRef.current.map(async document => {
				const { name, type } = document;
				const fileToUpload: File = document.originFileObj ?? document.file;
				const mimeType = fileToUpload.type;
				const mediaType = mimeType.split("/")[0];

				// Determine bucket path based on type
				let bucketSubPath = "documents";
				let linkField: "document_link" | "photo_link" | "video_link" | "audio_link" = "document_link";

				if (mediaType === "image") {
					bucketSubPath = "photos";
					linkField = "photo_link";
				} else if (mediaType === "video") {
					bucketSubPath = "videos";
					linkField = "video_link";
				} else if (mediaType === "audio") {
					bucketSubPath = "audios";
					linkField = "audio_link";
				}

				// Upload file
				const bucketName = `property-documents/${bucketSubPath}`;
				const fileName = createFirebaseFileName(fileToUpload, "property-document", name);
				const { publicUrl } = await uploadToStorage(fileToUpload, () => {}, `${bucketName}/${fileName}`);

				// Return document object for database
				return {
					property_id: propertyId,
					handover_id: handoverId,
					name,
					type,
					mime_type: mimeType,
					[linkField]: publicUrl
				} as Property_Documents_Insert_Input;
			});

			// Wait for all uploads to complete
			const uploadResults = await Promise.allSettled(uploadPromises);

			// Filter only successful uploads
			const documentsToInsert = uploadResults
				.filter(result => result.status === "fulfilled")
				.map(result => (result as PromiseFulfilledResult<Property_Documents_Insert_Input>).value);

			// Insert documents if any
			if (documentsToInsert.length > 0) {
				await addPropertyDocumentsMutation({
					variables: { objects: documentsToInsert }
				});
			}

			setUploading(false);
		} catch (error) {
			console.error("Error uploading documents:", error);
			setUploading(false);
			message.error("Failed to upload documents");
		}
	}, [propertyId, handoverId, addPropertyDocumentsMutation]);

	const resourcesCount = propertyDocuments.length;
	const isLoading = uploading || loadingAdd || loadingDelete;

	const handleOnClickDocument = useCallback((doc: any) => {
		const { document_link, video_link, photo_link } = doc;
		if (document_link) {
			setFileViewer({ type: MediaType.Document, src: document_link });
		} else if (video_link) {
			setFileViewer({ type: MediaType.Video, src: video_link });
		} else if (photo_link) {
			setFileViewer({ type: MediaType.Image, src: photo_link });
		}
	}, []);

	return (
		<>
			<div className={`hd-section${isAnon ? " hd-section--disabled" : ""}`}>
				<div className="hd-section-title">
					<div className="hd-section-title-number">3.</div> Additional Resources
				</div>
				<div className="hd-section-card">
					{(canEdit || isAnon) && (
						<>
							<p className="hd-section-description">
								Upload warranty documents, technical documentation, plans, drawings, or any other items
								related to your project at this property.
								<br />
								<strong>Tip:</strong> Anything related to a specific device, system or equipment should be added in the Devices and Equipment section above.
							</p>
							{isAnon ? (
								<p className="hd-section-description">
									<strong>Tip:</strong> Anything related to a specific device, system or piece of equipment should be
									added in the Devices and Equipment section above.
								</p>
							) : null}
						</>
					)}

					{/* Existing property documents */}
					{resourcesCount > 0 ? (
						<div className="hd-resources-list">
							{propertyDocuments.map(doc => {
								return (
									<div
										key={doc.id}
										className="hd-resource-row"
										style={{ cursor: "pointer" }}
										onClick={() => handleOnClickDocument(doc)}
									>
										<div className="hd-resource-icon">
											<svg width="20" height="20" viewBox="0 0 24 24" fill="#8c97af">
												<path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z" />
											</svg>
										</div>
										<div className="hd-resource-info">
											<span className="hd-resource-name">{doc.name || "Untitled"}</span>
										</div>
										<span className="hd-resource-badge">{getDocumentBadge(doc.mime_type)}</span>
										{canEdit && (
											<button
												className="hd-action-btn hd-action-btn--delete"
												title="Remove"
												onClick={e => {
													e.stopPropagation();
													handleDeleteExistingDocument(doc.id);
												}}
												disabled={isLoading}
											>
												<img src={`${process.env.PUBLIC_URL}/assets/images/icons/remove-trash.svg`} alt="Remove" />
											</button>
										)}
									</div>
								);
							})}
						</div>
					) : (
						!canEdit && <p className="hd-section-empty">No additional resources were added.</p>
					)}

					{/* New documents to upload */}
					{canEdit && documentsFiles.length > 0 && (
						<div className="attachments-container" style={{ marginTop: "16px" }}>
							{documentsFiles.map((documentFile, index) => {
								const uid = documentFile.uid || index;
								const { name, file, type, photo_link, video_link, document_link } = documentFile;

								return (
									<AttachmentItem
										key={`document-attachment-item-${uid}`}
										name={name}
										file={file}
										type={type}
										fileUrl={photo_link || video_link || document_link}
										onRemove={() => {
											if (!isLoading) {
												handleOnRemoveDocument(documentFile);
											}
										}}
										showEditButton
										onEdit={() => {
											if (!isLoading) {
												handleOnEditDocument(documentFile);
											}
										}}
									/>
								);
							})}
						</div>
					)}

					{/* Upload button */}
					{canEdit && (
						<div className="hd-resources-upload-area">
							<button
								className="hd-resources-upload-btn"
								onClick={() => setAddDocumentModalOpen(true)}
								disabled={isLoading}
								title="Add resources"
							>
								<img
									src={`${process.env.PUBLIC_URL}/assets/images/icons/plus-circle-purple.svg`}
									alt="Add files"
									className="hdd-dropzone-icon"
								/>
							</button>
						</div>
					)}
				</div>
			</div>

			<AddAttachmentModal
				open={addDocumentModalOpen}
				onCancel={handleOnCloseDocumentModal}
				onConfirm={handleUploadDocuments}
				updateAttachments={updateDocuments}
				editMode={addDocumentModalEditMode}
				editProduct={false}
				filesLimit={20}
			/>
			<MediaPlayer
				onCancel={() => setFileViewer({ type: null, src: "" })}
				src={fileViewer.src}
				open={!!fileViewer.src && fileViewer.type !== null}
				mediaType={fileViewer.type}
			/>
		</>
	);
};

export default HandoverAdditionalResources;
